import {Component, computed, effect, EventEmitter, input, Input, model, OnInit, Output, signal, untracked, ChangeDetectionStrategy} from '@angular/core';
import {Tile, TileFolderDisplayMode, TileId, TileIdMap} from '../../../../shared/models/tile';
import {TilesService} from '../../../../core/services/tiles.service';
import {DataPoint, SuperFolderStructure} from '../../../../shared/models/structure';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {Option, OptionGroupMultiSelect} from '../../../../shared/models/option';
import {TileOrderSorterComponent} from '../../tile-order-sorter/tile-order-sorter.component';
import {SpinnerComponent} from '../../spinner/spinner.component';

@Component({
  selector: 'motn-tile-folder-contents-editor',
  templateUrl: './tile-folder-contents-editor.component.html',
  styleUrls: ['./tile-folder-contents-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileOrderSorterComponent,
    SpinnerComponent,
  ],
})
export class TileFolderContentsEditorComponent implements OnInit {
  @Input() tile: Tile;

  // Input signal that increments when parent triggers cleanup
  cleanupDeletedTilesTrigger = input<number>(0);

  // Signals for reactive state
  orderedTileIds = signal<TileId[]>([]); // User's ordering (from Structure.TileIDs merged with FGA)
  originalTileIds = signal<TileId[]>([]);
  fgaValidTileIds = signal<TileId[]>([]); // Valid tiles from FGA (no ordering, excludes deleted)

  // Auto-computed diffs
  addedTiles = computed(() =>
    this.orderedTileIds().filter(id => !this.originalTileIds().includes(id))
  );

  removedTiles = computed(() =>
    this.originalTileIds().filter(id => !this.orderedTileIds().includes(id))
  );

  // Two-way binding with parent
  folderContentsData = model<{
    orderedTileIds: TileId[]; // User's ordering (may include deleted tiles)
    orderedValidTileIds: TileId[]; // User's ordering filtered to only valid tiles
    addedTiles: TileId[];
    removedTiles: TileId[];
  } | null>();

  allUserSimpleTiles: TileIdMap = {};
  selectableTilesOptionGroup: OptionGroupMultiSelect;
  isLoading: boolean = true;

  @Output() updatedOrder$: EventEmitter<TileId[]> = new EventEmitter<TileId[]>();
  @Output() updatedHiddenDataPoints$: EventEmitter<DataPoint[]> = new EventEmitter<DataPoint[]>();

  constructor(
    private tilesService: TilesService,
  ) {
    // Update parent whenever folder data changes (after initial load)
    effect(() => {
      // Track changes to orderedTileIds and fgaValidTileIds
      const ordered = this.orderedTileIds();
      const fgaValid = this.fgaValidTileIds();

      // Only update if data has been loaded (fgaValid populated)
      if (fgaValid.length > 0 || ordered.length > 0) {
        this.updateFolderData();
      }
    });

    // Cleanup deleted tiles when triggered by parent
    effect(() => {
      const trigger = this.cleanupDeletedTilesTrigger();
      if (trigger > 0) {
        untracked(() => this.removeDeletedTilesFromUI());
      }
    });
  }

  private updateFolderData(): void {
    // A valid tile is one that:
    // 1. Exists in allUserSimpleTiles (not deleted), AND
    // 2. Is either in FGA (existing child) OR newly added (not yet in FGA)
    const fgaValid = this.fgaValidTileIds();
    const added = this.addedTiles();
    const validTileIdsSet = new Set([...fgaValid, ...added].filter(tileId => this.allUserSimpleTiles[tileId]));

    // Preserve user's ordering but exclude deleted tiles and tiles not in FGA/added
    const orderedValidTileIds = this.orderedTileIds().filter(id => validTileIdsSet.has(id));

    this.folderContentsData.set({
      orderedTileIds: this.orderedTileIds(),
      orderedValidTileIds: orderedValidTileIds,
      addedTiles: this.addedTiles(),
      removedTiles: this.removedTiles()
    });
  }

  /**
   * Removes deleted tiles from the UI after save.
   * This updates orderedTileIds to only include valid tiles.
   * After save, FGA has been updated on backend, so we need to update our local FGA state.
   */
  public removeDeletedTilesFromUI(): void {
    // After save, orderedTileIds represents the new FGA state (added tiles now in FGA, removed tiles deleted)
    // Update fgaValidTileIds to reflect this new state
    const currentOrdered = this.orderedTileIds();
    const validCurrentOrdered = currentOrdered.filter(id => this.allUserSimpleTiles[id]);

    // Update FGA state to match what we just saved
    this.fgaValidTileIds.set(validCurrentOrdered);

    // Clean up deleted tiles (tiles not in allUserSimpleTiles)
    this.orderedTileIds.set(validCurrentOrdered);
    this.originalTileIds.set(validCurrentOrdered);
    this.buildTileSelectionOptions();
  }

  ngOnInit(): void {
    this.loadTiles();
  }

  /**
   * Loads and stores to this all tiles that can possibly be added to this
   * folder.
   *
   * @private
   */
  private loadTiles() {
    if (!this.tile.typeSupportsHavingDescendants()) {
      throw new Error('Unsupported tile type in loadTiles');
    }

    this.tilesService.fetchUserTiles$().subscribe({
      next: (tiles) => {
        const supportedTiles: TileIdMap = {};

        tiles.filter((t) => {
          return t.typeSupportsBeingInFolder();
        }).map((t) => {
          supportedTiles[t.ID] = t;
        });

        this.allUserSimpleTiles = supportedTiles;

        this.prepareTileGroups();
      },
      error: () => {
        // If fetchUserTiles fails, still try to load FGA data with empty user tiles
        this.allUserSimpleTiles = {};
        this.prepareTileGroups();
      }
    });
  }

  /**
   * Initializes orderedTileIds by merging FGA and Structure data.
   *
   * Important distinction:
   * - FGA = source of truth for WHICH tiles are children (relationship data)
   * - Structure.TileIDs = user-defined ORDER for displaying children
   *
   * Flow:
   * 1. Fetch FGA children (authoritative list of child relationships)
   * 2. Extract child IDs from response (all tiles except parent)
   * 3. Merge FGA children with Structure.TileIDs for ordering:
   *    - Start with Structure.TileIDs to maintain user's display order
   *    - Append FGA children not in Structure.TileIDs (newly added via bulk creation)
   * 4. Deleted tiles will be shown with "(deleted)" label
   *
   * @see buildTileSelectionOptions
   * @private
   */
  private prepareTileGroups() {
    // Fetch FGA children (source of truth for parent-child relationships)
    this.tilesService.fetchTilesAndDescendants$([this.tile.ID]).subscribe({
      next: (tiles) => {
        // Extract child IDs from FGA (source of truth for relationships, excludes deleted)
        const fgaChildIds = tiles
          .filter(t => t.ID !== this.tile.ID)
          .map(t => t.ID);

        // Store FGA valid tiles (excludes deleted)
        this.fgaValidTileIds.set(fgaChildIds);

        // Merge FGA children with Structure.TileIDs to preserve ordering:
        // Structure.TileIDs = user-defined display order (may be stale/incomplete)
        // FGA children = authoritative relationship data (always current)
        const structureTileIds = this.structure.TileIDs || [];
        const mergedTileIds = [
          ...structureTileIds,
          ...fgaChildIds.filter(id => !structureTileIds.includes(id))
        ];

        this.orderedTileIds.set(mergedTileIds);
        this.originalTileIds.set([...this.structure.TileIDs]); // Keep original for diff calculation

        // Rebuild UI options with new data
        this.buildTileSelectionOptions();
        this.isLoading = false;
      },
      error: () => {
        this.fgaValidTileIds.set([]);
        this.orderedTileIds.set([...this.structure.TileIDs]);
        this.originalTileIds.set([...this.structure.TileIDs]);
        this.buildTileSelectionOptions();
        this.isLoading = false;
      }
    });
  }


  /**
   * Builds the tile selection dropdown options including deleted tiles with "(deleted)" label.
   * Deleted tiles are tiles that exist in orderedTileIds but not in allUserSimpleTiles.
   *
   * @private
   */
  private buildTileSelectionOptions() {
    const options: Option[] = [];
    const currentIds = this.orderedTileIds();

    // Add existing tiles
    Object.values(this.allUserSimpleTiles).forEach((t) => {
      const facets = t.facets;
      options.push({
        id: '' + t.ID,
        name: t.Name,
        subtitle: facets.length ? facets.map(f => f.Value).join(' · ') : undefined,
        selected: currentIds.includes(t.ID),
        callback: () => {
          this.toggleTile(t.ID);
        },
        pill: {
          letter: 't',
          text: '' + t.ID,
        },
      });
    });

    // Add deleted tiles (in orderedTileIds but not in allUserSimpleTiles)
    currentIds.forEach((tileId) => {
      if (!this.allUserSimpleTiles[tileId]) {
        options.push({
          id: '' + tileId,
          name: `(deleted) will be removed on next save`,
          selected: true,
          callback: () => {
            this.toggleTile(tileId);
          },
          pill: {
            letter: 't',
            text: '' + tileId,
          },
        });
      }
    });

    this.selectableTilesOptionGroup = new OptionGroupMultiSelect(options);
  }

  /**
   *
   * @param tileId
   * @private
   */
  private toggleTile(tileId: TileId) {
    if (this.isDataPointsViewMode) {
      this.emptyDataPointsCheck();
      const isDataPoint = this.dataPoints?.find(tile => tile.TileID === tileId);
      if (isDataPoint) {
        this.dataPoints = this.dataPoints.filter(point => point.TileID !== tileId);
      } else {
        const defaultDataPoint = {TileID: tileId, Hidden: false};
        this.dataPoints = [...this.dataPoints, defaultDataPoint];
      }
    }

    this.orderedTileIds.update(ids => {
      const currentIndex = ids.indexOf(tileId);
      if (currentIndex > -1) {
        // Remove tile
        return [...ids.slice(0, currentIndex), ...ids.slice(currentIndex + 1)];
      } else {
        // Add tile
        return [...ids, tileId];
      }
    });

    this.buildTileSelectionOptions();
    this.emitChanges();
  }

  /**
   *
   * @param tileId
   */
  removeTile(tileId: TileId) {
    if (this.isDataPointsViewMode) {
      this.emptyDataPointsCheck();
      const isDataPoint = this.dataPoints?.find(tile => tile.TileID === tileId);
      if (isDataPoint) {
        this.dataPoints = this.dataPoints.filter(point => point.TileID !== tileId);
      }
    }

    this.orderedTileIds.update(ids => ids.filter(id => id !== tileId));

    this.buildTileSelectionOptions();
    this.emitChanges();
  }


  emptyDataPointsCheck() {
    const isDataPointsEmpty = !this.dataPoints?.length;
    if (isDataPointsEmpty) {
      this.dataPoints = this.defaultDataPointsArray;
    }
  }
  get defaultDataPointsArray(): DataPoint[] {
    return this.orderedTileIds().map(tileId => ({ TileID: tileId, Hidden: false}));
  }

  /**
   * Called whenever the user drags and drops a tile to a new position
   *
   * @param $event
   */
  drop($event: CdkDragDrop<TileId[]>) {
    // $event.container.data has been mutated by moveItemInArray in tile-order-sorter
    // Update the orderedTileIds signal with the new order
    this.orderedTileIds.set([...$event.container.data]);
    this.emitChanges();
  }

  onSortedById(sortedTileIds: TileId[]) {
    this.orderedTileIds.set(sortedTileIds);
    this.emitChanges();
  }

  /**
   *
   * @private
   */
  private emitChanges() {
    this.updatedOrder$.emit(this.orderedTileIds());
  }

  private get structure(): SuperFolderStructure {
    return this.tile.Structure as SuperFolderStructure;
  }

  get isDataPointsViewMode(): boolean {
    return this.structure?.DisplayMode === TileFolderDisplayMode.DataPoints;
  }

  get dataPoints(): DataPoint[] {
    return this.structure?.DataPoints;
  }

  set dataPoints(points: DataPoint[]) {
    this.structure.DataPoints = points;
  }

  onHiddenDataPointsUpdate($event: DataPoint[]) {
    this.dataPoints = $event
    this.updatedHiddenDataPoints$.emit($event);
  }
}
